import { useEffect, useRef, useState } from 'react';
import { submit } from '@/api';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2 } from 'lucide-react';
import { useStrings, t } from '@/hooks/useStrings';
import type { Submission } from '@/types';

type Props = {
	confirmedAt: string | null;
	previousSubmission: Submission;
	onRestart: (submission: Submission) => void;
	onFinished: () => void;
	onError: () => void;
};

const formatDateTime = (iso: string | null): string => {
	if (!iso) return '';
	try {
		const date = new Date(iso);
		if (Number.isNaN(date.getTime())) return '';
		return new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' }).format(date);
	} catch {
		return '';
	}
};

export const Success = ({ confirmedAt, previousSubmission, onRestart, onFinished, onError }: Props) => {
	const strings = useStrings();
	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const [reopening, setReopening] = useState(false);
	const [reopenError, setReopenError] = useState('');

	useEffect(() => {
		if (headingRef.current) headingRef.current.focus();
	}, []);

	const when = formatDateTime(confirmedAt) || formatDateTime(new Date().toISOString());
	const timestamp = when ? t(strings, 'success_timestamp', 'Empfangen am {when}.', { when }) : '';

	const handleReopen = async () => {
		if (reopening) return;
		setReopening(true);
		setReopenError('');
		try {
			const response = await submit({
				name: previousSubmission.name,
				order_number: String(previousSubmission.order_number),
				email: previousSubmission.email,
				reason: '',
				mocy_website: '',
				form_opened_at: Date.now(),
			});
			if (response.ok === true) {
				onRestart({
					order_id: response.order_id,
					order_number: response.order_number,
					token: response.token,
					line_items: response.line_items,
					total: response.total,
					currency: response.currency,
					expires_at: response.expires_at,
					name: previousSubmission.name,
					email: previousSubmission.email,
					reason: '',
				});
				return;
			}
			setReopenError(response.message || t(strings, 'success_reopen_error', 'Weitere Widerrufe für diese Bestellung sind nicht möglich.'));
		} catch (e) {
			console.error('[mocy-revocation] Success reopen threw', e);
			onError();
		} finally {
			setReopening(false);
		}
	};

	return (
		<section aria-live="polite" role="status">
			<Alert variant="default" className="tw:mb-4">
				<CheckCircle2 size={36} strokeWidth={1.15} className='tw:mb-1! tw:size-5!' />
				<AlertTitle ref={headingRef} tabIndex={-1} className="tw:text-lg">
					{t(strings, 'success_title', 'Widerruf bestätigt')}
				</AlertTitle>
				<AlertDescription>
					{t(strings, 'success_intro', 'Ihr Widerruf wurde empfangen. Eine Eingangsbestätigung wurde an die angegebene E-Mail-Adresse gesendet.')} {t(strings, 'success_disclaimer', 'Dies ist eine Empfangsbestätigung, kein Anerkenntnis. Die weitere Bearbeitung erfolgt durch unser Service-Team.')} <br /> {timestamp ? <p>{timestamp}</p> : null}
				</AlertDescription>
			</Alert>

			{reopenError ? (
				<Alert variant="destructive" className="tw:mb-4">
					<AlertDescription>{reopenError}</AlertDescription>
				</Alert>
			) : null}

			<div className="tw:flex tw:gap-2 tw:flex-wrap">
				<Button type="button" variant="outline" onClick={onFinished}>
					{t(strings, 'success_done', 'Fertig')}
				</Button>
			</div>
		</section>
	);
};
